import { useEffect, useRef, useState } from 'react';
import { services } from '../../../mocks/services';
import ServiceOrderModal from './ServiceOrderModal';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function ServicesSection() {
  const { ref, inView } = useInView();
  const [modalServiceId, setModalServiceId] = useState<number | null>(null);

  return (
    <>
      <section id="sluzby" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-sparkling-2-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Naše služby</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-4 leading-tight">
              Riešenia, ktoré{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                generujú zisk
              </span>
            </h2>
            <p className="text-zinc-500 text-lg max-w-xl mx-auto">
              Každá služba je navrhnutá s jediným cieľom - priniesť vám viac zákazníkov a vyšší obrat.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <ServiceCard
                key={service.id}
                service={service}
                delay={i * 80}
                inView={inView}
                onOrder={() => setModalServiceId(service.id)}
              />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className={`text-center mt-14 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-zinc-500 mb-5">Neviete, kde strácate zákazníkov? Nájdeme to zadarmo do 30 minút.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-700 text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Bezplatná analýza zadarmo
                <i className="ri-arrow-right-line" />
              </button>
              <button
                onClick={() => { const el = document.querySelector('#cennik'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 border border-zinc-200 hover:border-orange-400 bg-white hover:bg-orange-50 text-zinc-700 hover:text-orange-600 font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line" />
                Pozrieť cenník
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {modalServiceId !== null && (
        <ServiceOrderModal
          selectedServiceId={modalServiceId}
          onClose={() => setModalServiceId(null)}
        />
      )}
    </>
  );
}

interface ServiceCardProps {
  service: typeof services[0];
  delay: number;
  inView: boolean;
  onOrder: () => void;
}

function ServiceCard({ service, delay, inView, onOrder }: ServiceCardProps) {
  const [hovered, setHovered] = useState(false);

  const scrollToPricing = (categoryId: string) => {
    const el = document.querySelector('#cennik');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    // small delay to let scroll finish, then switch tab via custom event
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('pricing-switch', { detail: { categoryId } }));
    }, 600);
  };

  return (
    <div
      className={`group relative bg-white border border-zinc-100 rounded-2xl p-7 transition-all duration-500 overflow-hidden flex flex-col
        ${hovered ? '-translate-y-1 border-zinc-200' : ''}
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
      `}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Gradient background blob */}
      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />

      <div className="relative z-10 flex flex-col flex-1">
        {/* Icon + price row */}
        <div className="flex items-start justify-between mb-5">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-zinc-50 group-hover:bg-white transition-all duration-300">
            <i className={`${service.icon} text-2xl ${service.accent}`} />
          </div>
          {service.startingPrice && (
            <button
              onClick={() => scrollToPricing(service.pricingCategoryId)}
              className="flex flex-col items-end cursor-pointer group/price"
              title="Pozrieť cenník"
            >
              <span className={`text-sm font-black ${service.accent} group-hover/price:underline underline-offset-2 whitespace-nowrap`}>
                {service.startingPrice}
              </span>
              <span className="text-zinc-400 text-[11px] font-medium">{service.pricingNote}</span>
            </button>
          )}
        </div>

        {/* Title */}
        <h3 className="text-zinc-900 font-bold text-xl mb-3">{service.title}</h3>

        {/* Description */}
        <p className="text-zinc-500 text-sm leading-relaxed mb-5 flex-1">{service.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-6">
          {service.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs text-zinc-400 bg-zinc-50 group-hover:bg-white/70 border border-zinc-100 rounded-full px-3 py-1 font-medium transition-all duration-200"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Buttons row */}
        <div className="flex gap-2">
          <button
            onClick={onOrder}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap bg-white hover:bg-orange-500 border-zinc-200 hover:border-orange-500 text-zinc-700 hover:text-white group-hover:border-orange-500/40"
          >
            <i className="ri-mail-send-line" />
            Objednať
          </button>
          <button
            onClick={() => scrollToPricing(service.pricingCategoryId)}
            className="w-10 h-10 flex items-center justify-center rounded-xl border border-zinc-200 hover:border-orange-400 bg-white hover:bg-orange-50 text-zinc-400 hover:text-orange-500 transition-all duration-200 cursor-pointer flex-shrink-0"
            title="Pozrieť ceny"
          >
            <i className="ri-price-tag-3-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
